import type { TFunction } from "i18next";

/**
 * Wire shape of `GET /employee-changelog/departments/{departmentId}/shift-lineage`.
 *
 * IMPORTANT — mixed casing (confirmed against `backend/models/hris/lineage.py` +
 * `models/hris/sync.py`): the response serializes via `InHouseBaseModel`, which has NO
 * `alias_generator`, so FastAPI's default `by_alias=True` only camelCases fields that carry an
 * explicit `Field(alias=...)`. Every other field keeps its Python snake_case name. Do NOT "fix" the
 * snake_case keys to camelCase — they are the actual keys on the wire.
 *   - aliased → camelCase: `punchIdentity`, `fileS3Key`, `runStartedAt`
 *   - not aliased → snake_case: everything inside `change` and each punch identity
 */
export type SyncChangeAction = "insert" | "delete" | "skip" | "ignore" | "defer";

export interface LineagePunchIdentity {
	source: string;
	external_employee_id: string | null;
	hris_department_id: string | null;
	event_time: string | null;
	event_type: string | null;
	raw_earning_codes: string[] | null;
	pair_token: string | null;
	actual_punch_id: string | null;
}

export interface LineageChange {
	id: string;
	sync_run_id: string;
	created_at: string;
	action: SyncChangeAction;
	target_table: string;
	target_row_id: string | null;
	/** Enum slug (e.g. "unmapped_hris_department"). Mapped to a friendly label via `reasonLabel`. */
	reason: string;
	punchIdentity: LineagePunchIdentity[];
	/** Genuinely heterogeneous per reason (blocker ids, windows, counts). */
	detail: Record<string, unknown>;
}

export interface NurseShiftLineageEntry {
	change: LineageChange;
	source: string;
	/** Durable archived S3 object; NULL for API sources / dry-run captures. Not a working download link yet. */
	fileS3Key: string | null;
	runStartedAt: string;
}

/**
 * Reason slug → i18n key (product copy). A slug NOT listed here (a new backend reason) falls back to a
 * humanized version of the slug via `reasonLabel`, so the UI never crashes on an unknown value.
 */
const REASON_LABEL_KEY: Record<string, string> = {
	punch_derived_insert: "employeeChangelog.shiftLineage.reason.punch_derived_insert",
	absent_from_feed: "employeeChangelog.shiftLineage.reason.absent_from_feed",
	blocked_nurse_wide_shift: "employeeChangelog.shiftLineage.reason.blocked_nurse_wide",
	blocked_nurse_wide_external: "employeeChangelog.shiftLineage.reason.blocked_nurse_wide",
	unmapped_hris_department: "employeeChangelog.shiftLineage.reason.unmapped_hris_department",
	unmapped_event_type: "employeeChangelog.shiftLineage.reason.unmapped_event_type",
	missing_event_time: "employeeChangelog.shiftLineage.reason.missing_event_time",
	ignored_earning_code: "employeeChangelog.shiftLineage.reason.ignored_earning_code",
	long_shift: "employeeChangelog.shiftLineage.reason.long_shift",
	duplicate_collapsed_by_priority: "employeeChangelog.shiftLineage.reason.duplicate_collapsed_by_priority",
	unmatched_no_employee: "employeeChangelog.shiftLineage.reason.unmatched_no_employee",
	out_of_insert_window: "employeeChangelog.shiftLineage.reason.out_of_insert_window",
	boundary_day_delete_deferred: "employeeChangelog.shiftLineage.reason.boundary_day_delete_deferred",
	overlap_superseded: "employeeChangelog.shiftLineage.reason.overlap_superseded",
};

/** Humanize an unknown slug: "some_new_reason" → "Some new reason". Never crashes on a new value. */
export const humanizeSlug = (slug: string): string => {
	const spaced = slug.replace(/_/g, " ").trim();
	if (!spaced) return slug;
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/** Friendly translated label for a reason slug, with a humanized fallback for unknown slugs. */
export const reasonLabel = (t: TFunction, reason: string): string => {
	const key = REASON_LABEL_KEY[reason];
	return key ? t(key) : humanizeSlug(reason);
};
